import {Injectable} from "@angular/core";
import {DeviceDefectModel, deviceDefectModelFactory} from "../../device-defect/device-defect.model";
import {DefectAssetBundleModel, defectAssetBundleModelListFactory} from "./defect-asset-bundle.model";
import {OrderPartsModel, orderPartsModelListFactory, OrderPartsSubModel} from "./order-parts.model";
import {KeyByObject} from "../../misc/reduce-helpers";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";

@Injectable()
export class DeviceDefectWorkflowService {
  private url: string = "/api/devicedefectworkflow";

  constructor(private http: HttpClient) {}

  updateDefectDetails(deviceDefect: DeviceDefectModel): Promise<boolean> {
    return this.http.post(`${this.url}/update`, {
      id: deviceDefect.id,
      name: deviceDefect.name,
      siteId: deviceDefect.siteId,
    })
      .toPromise()
      .then(() => true);
  }

  updateAssetsForDeviceDefect(deviceDefectId: string, assetIds: string[], accessoryIds: string[]): Promise<boolean> {
    return this.http.post(
      `${this.url}/update-assets-for-device-defect`,
      {deviceDefectId, assetIds, accessoryIds},
    )
      .pipe(map(json => json["success"]))
      .toPromise();
  }

  getDefectAssets(deviceDefectId: string):
    Promise<{assets: DefectAssetBundleModel[], closed: boolean, orderId: string, deploymentId: string}> {
    return this.http.get(`${this.url}/get-assets?deviceDefectId=${deviceDefectId}`)
      .pipe(map((json: any) => {
        const assets = defectAssetBundleModelListFactory(json.assets);
        const orderParts = orderPartsModelListFactory(json.orderParts);
        const closed = !!json.closed;

        const orderPartsIndex = orderParts.reduce((obj, item) => obj.add(item.id, item), new KeyByObject())
          .toObject() as {[key: string]: OrderPartsModel};

        assets.forEach((asset) => {
          const {parts, reorderFullBundle} = orderPartsIndex[asset.id] ||
            {parts: [], reorderFullBundle: true} as OrderPartsModel;

          asset.reorderFullBundle = reorderFullBundle;

          if (reorderFullBundle) {
            asset.accessories.forEach((accessory) => {
              accessory.selected = true;
            });

            asset.standaloneAccessories.forEach((accessory) => {
              accessory.selected = true;
            });
          } else {
            const bundledAccessoriesIndex = (parts || []).reduce(
              (obj, item) => obj.add(`${item.entity}|${item.productNumber}|${item.serialNumber}`, true),
              new KeyByObject(),
            ).toObject();

            asset.accessories.forEach((accessory) => {
              const key = `Accessory|${accessory.productNumber}|${accessory.serialNumber}`;
              accessory.selected = bundledAccessoriesIndex[key] || false;
            });

            asset.standaloneAccessories.forEach((accessory) => {
              accessory.selected = accessory.id && Object.prototype.hasOwnProperty.call(orderPartsIndex, accessory.id);
            });
          }
        });

        return {assets, closed, orderId: json.orderId || "", deploymentId: json.deploymentId || ""};
      }))
      .toPromise();
  }

  public isDefectClosed(deviceDefectId: string) {
    return this.http.get(`${this.url}/is-defect-closed?deviceDefectId=${deviceDefectId}`);
  }

  finalize(deviceDefectId: string, orderName: string, deploymentName: string): Promise<DeviceDefectModel> {
    return this.http.post(`${this.url}/finalize`, {deviceDefectId, orderName, deploymentName})
      .pipe(map(json => deviceDefectModelFactory(json)))
      .toPromise();
  }

  async saveBundlePartSelections(deviceDefectId: string, assets: DefectAssetBundleModel[]): Promise<boolean> {
    const orderParts: OrderPartsModel[] = [];

    assets.forEach((asset) => {
      if (asset.reorderFullBundle) {
        orderParts.push({id: asset.id, reorderFullBundle: true, parts: []} as OrderPartsModel);

        asset.standaloneAccessories.forEach((accessory) => {
          orderParts.push({id: accessory.id, reorderFullBundle: true, parts: []} as OrderPartsModel);
        });
      } else {
        const parts = asset.accessories.filter(part => part.selected)
          .map(it => ({productNumber: it.productNumber, entity: "Accessory", serialNumber: ""} as OrderPartsSubModel));

        orderParts.push({id: asset.id, reorderFullBundle: false, parts} as OrderPartsModel);

        asset.standaloneAccessories.forEach((accessory) => {
          if (accessory.selected) {
            const accessoryPart = {
              productNumber: accessory.productNumber,
              serialNumber: accessory.serialNumber,
              entity: "StandaloneAccessory",
            } as OrderPartsSubModel;
            orderParts.push({id: accessory.id, reorderFullBundle: false, parts: [accessoryPart]});
          }
        });
      }
    });

    return this.http.post(`${this.url}/save-bundle-part-selections`, {deviceDefectId, orderParts})
      .pipe(map(json => json["success"]))
      .toPromise();
  }
}
